/* THE FRAME. The world, always; the site's toolbar over it; and whatever the page is, laid over the world. Home and a bed are the world itself. Every other page is a sheet that slides in beside it. Nothing else stays on screen. Above every page, one quiet line when the server is not answering or the licence is not in force (model/shell.ts). */
import { Component, Suspense, lazy, useEffect, useRef, useState, type ReactNode } from 'react'
import { X } from '@phosphor-icons/react'
import { Link, navigate, useLocation } from '../lib/router.tsx'
import { StatusLine, openTo, useStatus } from './ui/primitives.tsx'
import { CommandLine, Defs, WardPlan } from './ui/ward.tsx'
import { KeysPanel, useKeys } from './ui/keys.tsx'
import { useAsync } from '../lib/hooks.tsx'
import { refusalOf, v1 } from './api.ts'
import { partyRefs } from './model/office.ts'
import { useLicence, useMembership, usePublicWorld, useRuns } from './context.tsx'
import { shellBanner } from './model/shell.ts'
import { showProgram } from './model/program.ts'
import { STAGE_PATHS, bedWords, sheetWidth, BEDS } from './model/stage.ts'
import { isTyping } from './model/shortcuts.ts'
import { baysOf, type BedMark } from './model/ward.ts'
import { signOut } from './signout.ts'
import { previewOn } from './preview.ts'
import { useTheme } from '../lib/hooks.tsx'
import { StageProvider, useStage } from './ui/stage.tsx'

const StageCanvas = lazy(() => import('./ui/StageCanvas.tsx'))

export const FALLBACK_TEXT = 'This browser can’t draw the 3D world, so this is the floor plan. Select a bed to open it.'

class Guard extends Component<{ children: ReactNode; onFail: () => void }, { failed: boolean }> {
  override state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  override componentDidCatch() {
    this.props.onFail()
  }
  override render() {
    return this.state.failed ? null : this.props.children
  }
}

const MARK_OF = { died: 'lost', harm: 'harm', short: 'short', well: 'done' } as const

/** The world, or where WebGL is not there, its plan. */
function World() {
  const { count, body } = useStage()
  const world = usePublicWorld()
  const [failed, setFailed] = useState(false)
  if (failed) {
    const words = bedWords(body)
    const marks: BedMark[] = Array.from({ length: count }, (_, i) => MARK_OF[words.get(i + 1) ?? 'well'] as BedMark)
    return (
      <div className="stage stage-plan" data-survey="fallback">
        <p className="survey-fallback" role="status">{FALLBACK_TEXT}</p>
        <WardPlan count={count} marks={marks} minutes={world.data ? baysOf(world.data.floors ?? [])?.minutes ?? null : null} caption="World 001" />
      </div>
    )
  }
  return (
    <Guard onFail={() => setFailed(true)}>
      <Suspense fallback={<div className="stage" aria-busy="true" />}>
        <StageCanvas onFallback={() => setFailed(true)} />
      </Suspense>
    </Guard>
  )
}

/** The toolbar: the site's, the same height and the same three parts. */
function Toolbar({ program }: { program: boolean }) {
  const { path } = useLocation()
  const on = (test: boolean) => (test ? 'page' : undefined)
  return (
    <header className="bar">
      <div className="bar-start">
        <Link to="/" className="wordmark" aria-label="Damaros, World 001">Damaros</Link>
      </div>
      <nav className="bar-nav" aria-label="Console">
        <Link to="/" aria-current={on(STAGE_PATHS(path) && path !== '/connect')}>World 001</Link>
        <Link to="/runs" aria-current={on(path.startsWith('/runs') || path === '/compare' || path.startsWith('/bench'))}>Runs</Link>
        <Link to="/agents" aria-current={on(path.startsWith('/agents'))}>Residents</Link>
        {program ? <Link to="/program" aria-current={on(path === '/program' || path.startsWith('/verdict/'))}>Program</Link> : null}
        <Link to="/license" aria-current={on(path === '/license' || path === '/licence' || path === '/account')}>License</Link>
      </nav>
      <div className="bar-end">
        <Link to="/connect" className="btn btn-ink">Connect Resident</Link>
      </div>
    </header>
  )
}

/** A page beside the world. It slides in when one opens, and when the reader leaves it slides out holding what it showed. */
function Sheet({ path, children }: { path: string; children: ReactNode | null }) {
  const open = children !== null
  const held = useRef<{ path: string; node: ReactNode } | null>(null)
  const [leaving, setLeaving] = useState(false)
  const scroller = useRef<HTMLDivElement>(null)
  const [viewport, setViewport] = useState(() => window.innerWidth)
  useEffect(() => {
    const on = () => setViewport(window.innerWidth)
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  if (open) held.current = { path, node: children }
  useEffect(() => {
    if (open) {
      setLeaving(false)
      return
    }
    if (!held.current) return
    setLeaving(true)
    const t = window.setTimeout(() => {
      held.current = null
      setLeaving(false)
    }, 200)
    return () => window.clearTimeout(t)
  }, [open])
  useEffect(() => {
    if (open && !window.location.hash) scroller.current?.scrollTo({ top: 0 })
  }, [path, open])
  const shown = held.current
  if (!open && !leaving) return null
  if (!shown) return null
  const width = sheetWidth(shown.path, viewport)
  return (
    <aside className="sheet" ref={scroller} data-leaving={leaving ? 'yes' : undefined} data-whole={width >= viewport ? 'yes' : undefined} style={{ width }}>
      <Link to="/" className="sheet-close" aria-label="Back to World 001" aria-keyshortcuts="Escape"><X size={16} /></Link>
      <div className="sheet-body" key={shown.path}>{shown.node}</div>
    </aside>
  )
}

function useHashScroll(path: string): void {
  useEffect(() => {
    let timer = 0
    const go = () => {
      window.clearInterval(timer)
      const id = decodeURIComponent(window.location.hash.slice(1))
      if (!id) return
      let tries = 0
      timer = window.setInterval(() => {
        // An address that names a part inside a disclosure opens it.
        const el = openTo(id)
        if (el) {
          el.scrollIntoView({ block: 'start' })
          window.clearInterval(timer)
        } else if (++tries > 40) window.clearInterval(timer)
      }, 100)
    }
    go()
    window.addEventListener('hashchange', go)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('hashchange', go)
    }
  }, [path])
}

function StageKeys() {
  const { path } = useLocation()
  const { at, dots, scene, bed, select } = useStage()
  const latest = useRef({ at, dots, scene, bed, select, path })
  latest.current = { at, dots, scene, bed, select, path }
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return
      const s = latest.current
      if (e.key === 'Escape') {
        // What is open closes first: the keys, then the section the focus is in (ui/keys.tsx).
        if (document.getElementById('keys-panel') || document.activeElement?.closest('details[open]')) return
        if (STAGE_PATHS(s.path)) {
          if (s.path === '/connect') document.querySelector<HTMLButtonElement>('#connect .layer-close')?.click()
          else if (s.bed !== null) s.select(null)
          else return
        } else navigate('/')
        e.preventDefault()
        return
      }
      if (!STAGE_PATHS(s.path)) return
      if (e.key === 'n' || e.key === 'p' || e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const forward = e.key === 'n' || e.key === 'ArrowRight'
        // With a bed open, the next or the previous bed; otherwise the next or the previous case. The agent goes there.
        if (s.bed !== null) {
          s.select(((s.bed - 1 + (forward ? 1 : BEDS - 1)) % BEDS) + 1)
          e.preventDefault()
          return
        }
        if (!s.dots.length) return
        const i = (s.at + (forward ? 1 : s.dots.length - 1)) % s.dots.length
        s.scene.current?.jump(i)
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [])
  return null
}

export function Chrome({ children }: { children: ReactNode }) {
  const { path } = useLocation()
  const theme = useTheme()
  const world = usePublicWorld()
  const licence = useLicence()
  const runs = useRuns()
  const program = showProgram(useMembership().data)
  const say = useStatus()
  useHashScroll(path)
  const keys = useKeys()
  const banner = shellBanner(path, world.error ? refusalOf(world.error) : null, licence.state)
  const w = world.data
  const beds = w ? (baysOf(w.floors ?? [])?.count ?? 0) : 0
  const partyList = useAsync((sig) => (program ? v1.programParties(sig) : Promise.resolve(null)), [program, path])
  const partyVerdicts = useAsync((sig) => (program ? v1.programVerdicts(sig) : Promise.resolve(null)), [program, path])
  const parties = partyList.data ? partyRefs(partyList.data.parties, partyVerdicts.data) : []
  const ctx = { beds, runs: runs.data?.runs ?? [], program, parties, bedsideRun: null }
  const act = (a: 'dark' | 'light' | 'signout') => {
    if (a === 'signout') void signOut(say)
    else if (theme.resolved !== a) theme.set(a)
  }
  const onStage = STAGE_PATHS(path)
  return (
    <StageProvider>
      <a href="#main" className="skip-link">Skip to content</a>
      <Defs />
      <World />
      <Toolbar program={program} />
      {previewOn() ? <p className="preview-band" role="status">Preview on made-up records. Nothing here is a result. <a href="/?preview=off">Leave preview</a></p> : null}
      <div className="palette"><CommandLine ctx={ctx} onAct={act} /></div>
      {keys.open ? <KeysPanel panel={keys.panel} onClose={keys.hide} /> : null}
      <main id="main" className="over" tabIndex={-1}>
        {banner ? (
          <div className={`notice is-${banner.tone} over-notice`} role="status">
            <span>{banner.text}</span>
            {banner.link ? <Link to={banner.link.to} className="btn btn-sm btn-ghost">{banner.link.label}</Link> : null}
            {banner.retry ? <button className="btn btn-sm btn-ghost" onClick={world.reload}>Retry</button> : null}
          </div>
        ) : null}
        <div className="over-status"><StatusLine /></div>
        {onStage ? children : null}
        <Sheet path={path}>{onStage ? null : children}</Sheet>
      </main>
      <StageKeys />
    </StageProvider>
  )
}
